// src/config/coursePackages.js

export const COURSE_PACKAGES = {
  course_6week_group: {
    id: 'course_6week_group',
    name: 'My Roots - Arabic Together',
    description: '6-week group Arabic course',
    price: 87,
    duration: { weeks: 6, days: 42 },
    earlyRenewal: { weeks: 1, days: 7 },
    stripe: { priceId: 'price_1S2gwFH9rhxL9ztTkJNJiRaY', currency: 'CAD' },
    features: [
      '6 live online classes with a Professional Arabic teacher',
      'Small group classes (max 3 students)',
      'Access to 12+ interactive language and culture games',
      'AI Arabic tutor for unlimited learning',
      'Progress tracking and analytics',
      'Certificate of Completion'
    ],
    popular: true,
    maxStudents: 3,
    type: 'group',
    audience: 'child',
    shortLabel: 'Group',
    subtitle: 'Up to 3 students',
    cardDescription: 'Small group classes • Social learning • Access to online Arabic games',
    sortOrder: 1
  },
  course_6week_private: {
    id: 'course_6week_private',
    name: 'My Roots - Arabic One-to-One',
    description: '6-week Arabic course with private teacher',
    price: 130.00,
    duration: { weeks: 6, days: 42 },
    earlyRenewal: { weeks: 1, days: 7 },
    stripe: { priceId: 'price_1S2h78H9rhxL9ztTEm9Pl3Ey', currency: 'CAD' },
    features: [
      '6 live one-on-one classes with a Professional Arabic teacher',
      'Personalized curriculum and pacing',
      'Flexible scheduling options',
      'Access to 12+ fun interactive language and culture games',
      'AI Arabic tutor for unlimited learning',
      'Progress tracking and analytics',
      'Certificate of Completion'
    ],
    popular: false,
    maxStudents: 1,
    type: 'private',
    audience: 'child',
    shortLabel: 'Private',
    subtitle: '1-on-1 tutor',
    cardDescription: 'One-on-one instruction • Personalized pacing • Access to online Arabic games',
    sortOrder: 2
  },
  course_6week_group_accelerated: {
    id: 'course_6week_group_accelerated',
    name: 'My Roots - Arabic Accelerated',
    description: '6-week accelerated group Arabic course',
    type: 'group',
    audience: 'child',
    shortLabel: 'Accelerated',
    subtitle: 'Up to 3 students • 2x per week',
    cardDescription: 'Twice-weekly sessions • Accelerated progress • Access to online Arabic games',
    price: 165.00,
    duration: { weeks: 6, days: 42 },
    earlyRenewal: { weeks: 1, days: 7 },
    stripe: { priceId: 'price_1TMsFUH9rhxL9ztTNn22VB19', currency: 'CAD' },
    features: [
      '12 live online classes with a Professional Arabic teacher',
      'Two 1-hour sessions per week for accelerated progress',
      'Personalized curriculum and pacing',
      'Reach fluency milestones in half the time',
      'Access to 12+ fun interactive language and culture games',
      'AI Arabic tutor for unlimited learning',
      'Progress tracking and analytics',
      'Certificate of Completion'
    ],
    popular: false,
    maxStudents: 3,
    sortOrder: 3
  },
  course_6week_adult_group: {
    id: 'course_6week_adult_group',
    name: 'My Roots - Arabic for Adults',
    description: '6-week group Arabic course for adults',
    price: 95,
    duration: { weeks: 6, days: 42 },
    earlyRenewal: { weeks: 1, days: 7 },
    // Stripe product: prod_Mgwg5i5tAQ7eff (reference only — code uses priceId)
    stripe: { priceId: 'price_1LxYmxH9rhxL9ztTcJiwaeXp', currency: 'CAD' },
    features: [
      '6 live online classes with a Professional Arabic teacher',
      'Small group classes (max 3 adult learners)',
      'Conversational and practical Arabic for adults',
      'AI Arabic tutor for unlimited practice',
      'Progress tracking',
      'Certificate of Completion'
    ],
    popular: false,
    maxStudents: 3,
    type: 'group',
    audience: 'adult',
    shortLabel: 'Adult Group',
    subtitle: 'Adults • Up to 3 learners',
    cardDescription: 'Small group classes for adults • Conversational focus',
    sortOrder: 4
  }
};

// ✅ NEW: Helper function to convert Firestore Timestamps to JavaScript Dates
export function convertFirestoreDate(date) {
  if (!date) return null;
  
  // Handle Firestore Timestamp objects
  if (date && typeof date === 'object' && date.seconds) {
    return new Date(date.seconds * 1000);
  }
  
  // Handle regular Date objects or date strings
  if (date instanceof Date) {
    return date;
  }
  
  // Handle date strings
  if (typeof date === 'string' || typeof date === 'number') {
    return new Date(date);
  }
  
  return null;
}

export function findPackage(packageId) {
  return COURSE_PACKAGES[packageId];
}

export function listAllPackages() {
  return Object.values(COURSE_PACKAGES);
}

export function computeCourseTiming(packageId, startDate = new Date(), currentEndDate = null) {
  const pkg = findPackage(packageId);
  if (!pkg) throw new Error(`Package ${packageId} not found`);
  
  let start;
  if (currentEndDate) {
    start = new Date(currentEndDate);
    start.setDate(start.getDate() + 7);
  } else {
    start = new Date(startDate);
  }
  
  const end = new Date(start);
  end.setDate(start.getDate() + pkg.duration.days);
  
  const earlyRenewalDate = new Date(end);
  earlyRenewalDate.setDate(end.getDate() - pkg.earlyRenewal.days);
  
  return { start, end, earlyRenewalDate };
}

// ✅ FIXED: Now properly handles Firestore Timestamps
export function isEarlyRenewalPeriod(earlyRenewalDate, endDate) {
  if (!earlyRenewalDate || !endDate) return false;
  
  const now = new Date();
  const earlyDate = convertFirestoreDate(earlyRenewalDate);
  const endDateObj = convertFirestoreDate(endDate);
  
  if (!earlyDate || !endDateObj) return false;
  
  console.log('🔍 Early Renewal Check:', {
    now: now.toISOString(),
    earlyRenewalDate: earlyDate.toISOString(),
    endDate: endDateObj.toISOString(),
    isAfterEarlyDate: now >= earlyDate,
    isBeforeEndDate: now <= endDateObj
  });
  
  return now >= earlyDate && now <= endDateObj;
}

// ✅ FIXED: Now properly handles Firestore Timestamps
export function isCourseExpired(endDate) {
  if (!endDate) return false;
  const endDateObj = convertFirestoreDate(endDate);
  if (!endDateObj) return false;
  
  const now = new Date();
  const isExpired = now > endDateObj;
  
  console.log('🔍 Course Expired Check:', {
    now: now.toISOString(),
    endDate: endDateObj.toISOString(),
    isExpired
  });
  
  return isExpired;
}

// ✅ FIXED: Now properly handles Firestore Timestamps
export function canUserRenew(endDate, earlyRenewalDate) {
  const expired = isCourseExpired(endDate);
  const inEarlyPeriod = isEarlyRenewalPeriod(earlyRenewalDate, endDate);
  
  console.log('🔍 Can User Renew Check:', {
    expired,
    inEarlyPeriod,
    canRenew: expired || inEarlyPeriod
  });
  
  return expired || inEarlyPeriod;
}

export function computePrice(packageId, quantity = 1, discountAmount = 0) {
  const pkg = findPackage(packageId);
  if (!pkg) {
    return { package: null, quantity: 0, unitPrice: 0, subtotal: 0, discountAmount: 0, total: 0 };
  }
  
  const subtotal = pkg.price * quantity;
  const total = Math.max(0, subtotal - discountAmount);
  
  return {
    package: pkg,
    quantity,
    unitPrice: pkg.price,
    subtotal,
    discountAmount,
    total: Math.round(total * 100) / 100
  };
}

export function getSortedPackages() {
  return Object.values(COURSE_PACKAGES).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function checkPackage(packageId) {
  const pkg = findPackage(packageId);
  if (!pkg) {
    throw new Error(`Invalid package ID: ${packageId}`);
  }
  if (!pkg.stripe.priceId || pkg.stripe.priceId.includes('PLACEHOLDER')) {
    throw new Error(`Missing Stripe price ID for package: ${packageId}`);
  }
  return pkg;
}